import { useState } from 'react';
import { formatDistanceToNow, subHours } from 'date-fns';
import {
  Activity,
  AlertCircle,
  Check,
  Clock,
  DollarSign,
  Pencil,
  RefreshCw,
  TrendingUp,
  X,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import {
  usePayFeeSchedule,
  useUpdatePayTenantFee,
  type ClinicPayFeeRow,
} from '@/hooks/usePayTenant';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  useExternalCallLogList,
  useExternalCallLogStats,
  type ExternalCallLogRow,
  type TargetSystem,
} from '@/hooks/useExternalCallLog';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SYSTEM_LABELS: Record<TargetSystem, string> = {
  clinio_pay:   'Clinio Pay',
  clinio_forms: 'Clinio Forms',
};

const SYSTEM_COLORS: Record<TargetSystem, string> = {
  clinio_pay:   'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  clinio_forms: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
};

const STATUS_COLORS: Record<number, string> = {
  200: '#22c55e',
  300: '#3b82f6',
  400: '#f59e0b',
  500: '#ef4444',
};

function getStatusBadgeClass(status: number | null): string {
  if (status === null) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  if (status >= 200 && status < 300) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  if (status >= 300 && status < 400) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
  if (status >= 400 && status < 500) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatErrorRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Health card
// ---------------------------------------------------------------------------

function HealthCard({
  system,
  healthRow,
  isLoading,
}: {
  system:    TargetSystem;
  healthRow: ExternalCallLogRow | null;
  isLoading: boolean;
}) {
  const isHealthy =
    healthRow !== null &&
    healthRow.http_status !== null &&
    healthRow.http_status >= 200 &&
    healthRow.http_status < 300;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-3">
        <div className={`rounded-lg p-2.5 ${SYSTEM_COLORS[system]}`}>
          <Activity className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {SYSTEM_LABELS[system]}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Health check</p>
        </div>
        <div className="ml-auto">
          {isLoading ? (
            <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
          ) : healthRow === null ? (
            <span className="flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400">
              <WifiOff className="h-3.5 w-3.5" />
              No data yet
            </span>
          ) : isHealthy ? (
            <span className="flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-300">
              <Wifi className="h-3.5 w-3.5" />
              {healthRow.http_status} OK
            </span>
          ) : (
            <span className="flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
              <WifiOff className="h-3.5 w-3.5" />
              {healthRow.http_status ?? 'Error'}
            </span>
          )}
        </div>
      </div>

      {healthRow && (
        <div className="mt-3 flex items-center gap-4 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {formatDistanceToNow(new Date(healthRow.created_at), { addSuffix: true })}
          </span>
          <span className="flex items-center gap-1">
            <Zap className="h-3.5 w-3.5" />
            {formatDuration(healthRow.duration_ms)}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stats panel
// ---------------------------------------------------------------------------

function StatsPanel({
  targetSystem,
  direction,
  since,
}: {
  targetSystem: TargetSystem;
  direction:    'outbound' | 'inbound';
  since:        string;
}) {
  const { data: stats, isLoading } = useExternalCallLogStats({ targetSystem, since });

  // Recharts expects fill as a color string, not a Tailwind class
  const rechartsData = (stats?.statusBuckets ?? []).map((b) => {
    return {
      name:  b.status !== null ? `${b.status}xx` : 'Err',
      count: b.count,
      fill:  b.status !== null ? (STATUS_COLORS[b.status] ?? '#9ca3af') : '#9ca3af',
    };
  });

  if (isLoading && !stats) {
    return (
      <div className="flex items-center justify-center py-6">
        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPI row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
          <p className="mt-0.5 text-lg font-semibold text-gray-900 dark:text-white">
            {stats?.totalCount ?? 0}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">p95 latency</p>
          <p className="mt-0.5 text-lg font-semibold text-gray-900 dark:text-white">
            {formatDuration(stats?.p95DurationMs ?? null)}
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">Error rate</p>
          <p
            className={`mt-0.5 text-lg font-semibold ${
              (stats?.errorRate ?? 0) > 0.05
                ? 'text-red-600 dark:text-red-400'
                : 'text-green-600 dark:text-green-400'
            }`}
          >
            {formatErrorRate(stats?.errorRate ?? 0)}
          </p>
        </div>
      </div>

      {/* Status distribution chart */}
      {rechartsData.length > 0 ? (
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rechartsData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(value) => [value as number, 'calls']}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {rechartsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="py-4 text-center text-xs text-gray-400 dark:text-gray-500">
          No {direction} calls in the last 24 h
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Call log table
// ---------------------------------------------------------------------------

function CallLogTable({
  rows,
  isLoading,
  emptyLabel,
}: {
  rows:       ExternalCallLogRow[];
  isLoading:  boolean;
  emptyLabel: string;
}) {
  if (isLoading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center py-6">
        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-gray-400 dark:text-gray-500">{emptyLabel}</p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700/50">
          <tr>
            {['Time', 'Method', 'Endpoint', 'Status', 'Duration', 'Error'].map((h) => (
              <th
                key={h}
                className="px-3 py-2.5 text-left font-medium text-gray-500 dark:text-gray-400"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700/50 dark:bg-gray-800">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
              <td className="whitespace-nowrap px-3 py-2 text-gray-500 dark:text-gray-400">
                {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-gray-700 dark:text-gray-300">
                {row.http_method}
              </td>
              <td
                className="max-w-[220px] truncate px-3 py-2 font-mono text-gray-700 dark:text-gray-300"
                title={row.endpoint}
              >
                {row.endpoint}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <span
                  className={`inline-flex rounded px-1.5 py-0.5 font-semibold ${getStatusBadgeClass(row.http_status)}`}
                >
                  {row.http_status ?? '—'}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-500 dark:text-gray-400">
                {formatDuration(row.duration_ms)}
              </td>
              <td className="max-w-[180px] truncate px-3 py-2 text-gray-500 dark:text-gray-400" title={row.error_message ?? ''}>
                {row.error_code ? (
                  <span className="text-red-600 dark:text-red-400">{row.error_code}</span>
                ) : (
                  <span className="text-green-600 dark:text-green-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-system section
// ---------------------------------------------------------------------------

function SystemSection({ system }: { system: TargetSystem }) {
  const since = subHours(new Date(), 24).toISOString();

  const { data: outboundRows = [], isLoading: outLoading } = useExternalCallLogList({
    targetSystem: system,
    direction:    'outbound',
    limit:        50,
  });

  const { data: inboundRows = [], isLoading: inLoading } = useExternalCallLogList({
    targetSystem: system,
    direction:    'inbound',
    limit:        50,
  });

  // Last /health call
  const healthRow =
    outboundRows.find((r) => r.endpoint.includes('/health')) ?? null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          {SYSTEM_LABELS[system]}
        </h2>
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${SYSTEM_COLORS[system]}`}>
          integration
        </span>
      </div>

      {/* Health card */}
      <HealthCard system={system} healthRow={healthRow} isLoading={outLoading} />

      {/* Outbound */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Last 50 outbound calls
          </h3>
          <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">last 24 h stats</span>
        </div>
        <StatsPanel targetSystem={system} direction="outbound" since={since} />
        <div className="mt-4">
          <CallLogTable
            rows={outboundRows}
            isLoading={outLoading}
            emptyLabel="No outbound calls recorded yet"
          />
        </div>
      </div>

      {/* Inbound */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex items-center gap-2">
          <Activity className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Last 50 inbound webhooks
          </h3>
          <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">last 24 h stats</span>
        </div>
        <StatsPanel targetSystem={system} direction="inbound" since={since} />
        <div className="mt-4">
          <CallLogTable
            rows={inboundRows}
            isLoading={inLoading}
            emptyLabel="No inbound webhooks recorded yet"
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fee schedule — confirmation modal
// ---------------------------------------------------------------------------

function FeeConfirmModal({
  row,
  newFee,
  onConfirm,
  onCancel,
  isLoading,
}: {
  row:       ClinicPayFeeRow;
  newFee:    number;
  onConfirm: () => void;
  onCancel:  () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center gap-3">
          <div className="rounded-lg bg-amber-100 p-2.5 dark:bg-amber-900/30">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Confirm fee change
          </h3>
          <button
            onClick={onCancel}
            className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300">
          You are changing the platform fee for{' '}
          <span className="font-semibold text-gray-900 dark:text-white">{row.clinic_name}</span>{' '}
          from{' '}
          <span className="font-mono font-medium text-gray-900 dark:text-white">
            {row.platform_fee_percent.toFixed(2)}%
          </span>{' '}
          to{' '}
          <span className="font-mono font-medium text-amber-600 dark:text-amber-400">
            {newFee.toFixed(2)}%
          </span>
          .
        </p>

        <div className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          The new fee applies to future payments only. Existing checkouts and
          in-progress payments are not affected.
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Saving…
              </span>
            ) : (
              'Confirm change'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fee schedule — editable table row
// ---------------------------------------------------------------------------

function FeeRow({ row }: { row: ClinicPayFeeRow }) {
  const [editing, setEditing]       = useState(false);
  const [inputVal, setInputVal]     = useState(row.platform_fee_percent.toFixed(2));
  const [confirming, setConfirming] = useState(false);
  const [localFee, setLocalFee]     = useState<number | null>(null);

  const { mutate, isPending } = useUpdatePayTenantFee();

  const displayFee = localFee ?? row.platform_fee_percent;

  function handleEditClick() {
    setInputVal(displayFee.toFixed(2));
    setEditing(true);
  }

  function handleSaveClick() {
    const parsed = parseFloat(inputVal);
    if (isNaN(parsed) || parsed < 0 || parsed > 50) return;
    setConfirming(true);
  }

  function handleConfirm() {
    const newFee = parseFloat(inputVal);
    mutate(
      { clinic_id: row.clinic_id, platform_fee_percent: newFee },
      {
        onSuccess: () => {
          setLocalFee(newFee);
          setEditing(false);
          setConfirming(false);
        },
        onError: () => {
          setConfirming(false);
        },
      },
    );
  }

  const parsedInput = parseFloat(inputVal);
  const inputValid  = !isNaN(parsedInput) && parsedInput >= 0 && parsedInput <= 50;

  return (
    <>
      {confirming && (
        <FeeConfirmModal
          row={{ ...row, platform_fee_percent: displayFee }}
          newFee={parsedInput}
          onConfirm={handleConfirm}
          onCancel={() => setConfirming(false)}
          isLoading={isPending}
        />
      )}
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
          {row.clinic_name}
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-sm">
          {editing ? (
            <input
              type="number"
              min="0"
              max="50"
              step="0.01"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              className="w-24 rounded border border-gray-300 bg-white px-2 py-1 text-right font-mono text-sm text-gray-900 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              autoFocus
            />
          ) : (
            <span className="font-mono font-medium text-gray-900 dark:text-white">
              {displayFee.toFixed(2)}%
            </span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
          {row.last_changed_at
            ? formatDistanceToNow(new Date(row.last_changed_at), { addSuffix: true })
            : '—'}
        </td>
        <td className="whitespace-nowrap px-4 py-3">
          {editing ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveClick}
                disabled={!inputValid}
                className="inline-flex items-center gap-1 rounded bg-amber-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-40"
              >
                <Check className="h-3 w-3" />
                Save
              </button>
              <button
                onClick={() => setEditing(false)}
                className="inline-flex items-center gap-1 rounded border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <X className="h-3 w-3" />
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={handleEditClick}
              className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
            >
              <Pencil className="h-3 w-3" />
              Edit
            </button>
          )}
        </td>
      </tr>
    </>
  );
}

// ---------------------------------------------------------------------------
// Fee schedule section
// ---------------------------------------------------------------------------

function FeeScheduleSection() {
  const { data: rows = [], isLoading, error } = usePayFeeSchedule();

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-700">
        <div className="rounded-lg bg-amber-100 p-2.5 dark:bg-amber-900/30">
          <DollarSign className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Per-clinic fee schedule
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Platform fee charged on every Clinio Pay transaction. Default 5.00%.
            Changes apply to future payments only.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 px-5 py-4 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Could not load fee schedule.{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : rows.length === 0 ? (
        <p className="px-5 py-6 text-center text-sm text-gray-400 dark:text-gray-500">
          No active Pay tenants found.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                {['Clinic', 'Platform fee', 'Last changed', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700/50 dark:bg-gray-800">
              {rows.map((row) => (
                <FeeRow key={row.clinic_id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SYSTEMS: TargetSystem[] = ['clinio_pay', 'clinio_forms'];

export function Integrations() {
  const [activeSystem, setActiveSystem] = useState<TargetSystem>('clinio_pay');

  return (
    <div className="mx-auto max-w-6xl p-6">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Integrations</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Cross-system observability — every outbound call and inbound webhook
          from the last 90 days. Read-only.
        </p>
      </div>

      {/* Per-clinic fee schedule */}
      <div className="mb-8">
        <FeeScheduleSection />
      </div>

      {/* System tabs */}
      <div className="mb-6 flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {SYSTEMS.map((sys) => (
          <button
            key={sys}
            onClick={() => setActiveSystem(sys)}
            className={`-mb-px px-4 pb-3 pt-2 text-sm font-medium transition-colors ${
              activeSystem === sys
                ? 'border-b-2 border-amber-500 text-amber-600 dark:text-amber-400'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {SYSTEM_LABELS[sys]}
          </button>
        ))}
      </div>

      {/* Active system section */}
      <SystemSection key={activeSystem} system={activeSystem} />
    </div>
  );
}
